import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
  FormHelperText,
  FormControl,
  Tooltip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
} from '@mui/material';

import config from '../../js/config';
import { getBearerString } from '../../js/jwt_token';

type LFDFilterDialogProps = {
  open: boolean;
  onClose: () => void;
  onSaveCallback: (requestBody: any) => void;
};

const ActuatorTestFilterDialog = ({ open, onClose, onSaveCallback }: LFDFilterDialogProps) => {
  const filterDefaultValues = {
    actuator_type: [],
    actuator_name: [],
    actuator_serial_number: [],
    test_type: [],
    test_name: [],
    test_success: true,
    test_start_time: null,
    test_end_time: null,
    testbed_actuator_type: [],
    testbed_config: [],
  };

  const [distinctValues, setDistinctValues] = useState<any>(null);
  const [filters, setFilters] = useState(filterDefaultValues);

  useEffect(() => {
    const fetchDistinctValues = async () => {
      try {
        const response = await fetch(config.data_platform_api_url + '/actuator-tests/filter_options', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: getBearerString(),
          },
        });
        const data = await response.json();
        setDistinctValues(data);
      } catch (error) {
        console.error('Failed to fetch distinct values:', error);
      }
    };

    if (open && !distinctValues) {
      fetchDistinctValues();
    }
  }, [open, distinctValues]);

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const handleReset = () => {
    setFilters(filterDefaultValues);
    onSaveCallback({});
  };

  const onSave = () => {
    // Return Empty Body if equal to default values (see js/query_logs.js) to determine to query w/o filters
    if (JSON.stringify(filterDefaultValues) === JSON.stringify(filters)) {
      onSaveCallback({});
      return;
    }

    // Else Return Filters
    // Transform features val from [featureA, featureB...] {featureA: 1, featureB: 1...}
    var ret_filters = {
      ...filters,
    };

    // Transform Start and EndTime if there set -> epoch nanoseconds
    if (filters.test_start_time !== null) {
      ret_filters.test_start_time =
        new Date(filters.test_start_time).getTime() * 1e6;
    }
    if (filters.test_end_time !== null) {
      ret_filters.test_end_time = new Date(filters.test_end_time).getTime() * 1e6;
    }

    onSaveCallback(ret_filters);
    return;
  };

  const renderMultiSelect = (label: string, field: string) => (
    <FormControl fullWidth margin="normal">
      <Autocomplete
        multiple
        options={distinctValues?.[field]?.sort((a: string, b: string) => a.localeCompare(b)) ?? []}
        value={filters[field]}
        onChange={(_, value) => handleFilterChange(field, value)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            placeholder="Select or type..."
          />
        )}
      />
      <FormHelperText>Select multiple {label.toLowerCase()} values or start typing to filter.</FormHelperText>
    </FormControl>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Set Actuator Test Filters</DialogTitle>
      <DialogContent>
        {distinctValues ? (
          <>
            {renderMultiSelect("Actuator Type", "actuator_type")}
            {renderMultiSelect("Actuator Name", "actuator_name")}
            {renderMultiSelect("Actuator Serial Number", "actuator_serial_number")}
            {renderMultiSelect("Test Type", "test_type")}
            {renderMultiSelect("Test Name", "test_name")}
            {renderMultiSelect("Testbed Actuator Type", "testbed_actuator_type")}
            {renderMultiSelect("Testbed Config", "testbed_config")}

            <Tooltip title="Unchecked means failed or incomplete tests">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.test_success === true}
                      onChange={() =>
                        handleFilterChange('test_success', !filters.test_success)
                      }
                    />
                  }
                  label="Test Successful"
                />
              </FormGroup>
            </Tooltip>

            {/* Add optional numeric filters for start and end time */}
            <FormControl>
              <Box mb={2}>
                <Typography gutterBottom>Created Date Range</Typography>
                <TextField
                  label="Start Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={filters.test_start_time || ""}
                  onChange={(e) =>
                    handleFilterChange("test_start_time", e.target.value)
                  }
                  sx={{ mr: 2 }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={filters.test_end_time || ""}
                  onChange={(e) =>
                    handleFilterChange("test_end_time", e.target.value)
                  }
                />
              </Box>
            </FormControl>
          </>
        ) : (
          <DialogContentText>Loading filter options...</DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onSave} color="primary">
          Apply
        </Button>
        <Button onClick={handleReset} color="primary">
          Reset
        </Button>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActuatorTestFilterDialog;
